import { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Rectangle,
  type RectangleProps,
  ResponsiveContainer,
} from "recharts";
import styles from "./styles.module.scss";
import classNames from "classnames";
import dayjs from "dayjs";
import { RelativeComponentLoader } from "../../components/loader";
import { InfoIcon } from "@/assets/icons";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@/uiCore";
import { NativeSelect } from "@altimateai/lego";
import type { TopInfo, WarehouseAutoSavingsGraphItem } from "./types";

interface TimeFrameOption {
  label: string;
  value: number;
}

interface TimeFrameSelectorDropdownProps {
  options: TimeFrameOption[];
  period: number;
  setPeriod: (value: number) => void;
}

const TimeFrameSelectorDropdown = ({
  options,
  period,
  setPeriod,
}: TimeFrameSelectorDropdownProps) => {
  const getLabelByPeriod = (p: number) => {
    return options.find(opt => opt.value === p)?.label ?? String(p);
  };

  return (
    <NativeSelect
      id="graph-period-dropdown"
      onChange={value => setPeriod(parseInt(value, 10))}
      value={String(period)}
      options={options.map(p => ({
        label: p.label,
        value: String(p.value),
      }))}
      size="sm"
      label={getLabelByPeriod(period)}
    />
  );
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: Record<string, unknown> }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  const { currency } = useAppState();

  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const date = data.time;

    return (
      <div className={styles.custom_tooltip}>
        <div className={styles.date}>{date as string}</div>
        <div className={styles.total_cost}>
          Total Cost : {formatNumber(data.cost as number, { currency })}
        </div>

        <div className={styles.spend_section}>
          <div className={styles.spend_dot} />
          <div className={styles.content}>
            <div>
              Spend: <strong>{data.Spend as string}</strong>
            </div>
          </div>
        </div>

        <div className={styles.savings_section}>
          <div className={styles.heading}>
            <div className={styles.savings_dot} />
            <div>
              Possible Savings: <strong>{data.possibleTotalSavings as string}</strong>
            </div>
          </div>
          <div className={styles.breakdown}>
            <div>
              Warehouse Suspension + Scaling:{" "}
              <strong>{data.possibleWarehouseScalingAndSuspensionSavings as string}</strong>
            </div>
            <div>
              Warehouse Sizing: <strong>{data.possibleWarehouseSizingSavings as string}</strong>
            </div>
          </div>
        </div>

        <div className={styles.savings_section}>
          <div className={styles.heading}>
            <div className={styles.auto_tune_dot} />
            <div>
              Autotune Saving: <strong>{data.actualTotalSavings as string}</strong>
            </div>
          </div>
          <div className={styles.breakdown}>
            <div>
              Warehouse Suspension + Scaling:{" "}
              <strong>{data.actualWarehouseScalingAndSuspensionSavings as string}</strong>
            </div>
            <div>
              Warehouse Sizing: <strong>{data.actualWarehouseSizingSavings as string}</strong>
            </div>
          </div>
        </div>

        <div className={styles.info_section}>
          <InfoIcon />
          <div className={styles.text}>
            Change is calculated from the value in the previous period
          </div>
        </div>
      </div>
    );
  }

  return null;
};

interface AxisTickProps {
  x: number;
  y: number;
  payload: { value: number | string };
}

type BarShapeProps = RectangleProps & { payload?: Record<string, number> };

interface AutoManageGraphProps {
  period?: number;
  showPeriodDropdown?: boolean;
  data?: WarehouseAutoSavingsGraphItem[];
  topInfo: Record<string, TopInfo>;
  setPeriod?: ((value: number) => void) | null;
  defaultTimeFrame?: TimeFrameOption[] | null;
  resizingEnabled?: boolean;
}

const AutoManageGraph = ({
  period = 1,
  showPeriodDropdown = true,
  data = [],
  topInfo,
  setPeriod = null,
  defaultTimeFrame = null,
  resizingEnabled = false,
}: AutoManageGraphProps) => {
  const { currency } = useAppState();
  const [autoTuneFilter, setAutoTuneFilter] = useState("all");

  const timeframes: TimeFrameOption[] = defaultTimeFrame
    ? defaultTimeFrame
    : [
        { label: "Day", value: 1 },
        { label: "Week", value: 7 },
        { label: "Last 30 Days", value: 30 },
      ];

  const [timeframe, setTimeframe] = useState(period);

  const dropDownOnChange = (value: number) => {
    setTimeframe(value);
    if (setPeriod) setPeriod(value);
  };

  const formatDate = (date: string) => dayjs(date).format("DD-MMM-YY");

  const roundToTwoDecimalPlaces = (number: number) => {
    return Math.round(number * 100) / 100;
  };

  const getSavings = (min: number, max: number) => {
    if (min === max) {
      return `${formatNumber(min, { currency })}`;
    } else {
      return `${formatNumber(min, { currency })} - ${formatNumber(max, { currency })}`;
    }
  };

  const stackedData = useMemo(() => {
    if (data.length === 0) {
      console.log("AutoManageGraph: No data available.");
      return [];
    }
    return data.map(entry => ({
      time: formatDate(entry.date),
      cost: roundToTwoDecimalPlaces(entry.actual + entry.spend + entry.potential),
      "Auto Tune Savings": roundToTwoDecimalPlaces(entry.actual),
      "Possible Savings": roundToTwoDecimalPlaces(entry.potential),
      spend: roundToTwoDecimalPlaces(Math.max(entry.spend - entry.potential, 0)),
      actual: entry.actual,
      potential: entry.potential,
      possibleTotalSavings: getSavings(
        entry.min_total_potential_cost_saved,
        entry.max_total_potential_cost_saved
      ),
      possibleWarehouseSizingSavings: getSavings(
        entry.min_potential_resize_cost_saved,
        entry.max_potential_resize_cost_saved
      ),
      possibleWarehouseScalingAndSuspensionSavings: getSavings(
        entry.min_potential_cost_saved,
        entry.max_potential_cost_saved
      ),
      actualTotalSavings: getSavings(
        entry.min_total_actual_cost_saved,
        entry.max_total_actual_cost_saved
      ),
      actualWarehouseSizingSavings:
        resizingEnabled &&
        entry.max_actual_resize_cost_saved === 0 &&
        (dayjs(entry.date).isSame(dayjs(), "day") ||
          dayjs(entry.date).isSame(dayjs().subtract(1, "day"), "day"))
          ? "Calculating"
          : getSavings(entry.min_actual_resize_cost_saved, entry.max_actual_resize_cost_saved),
      actualWarehouseScalingAndSuspensionSavings: getSavings(
        entry.min_actual_cost_saved,
        entry.max_actual_cost_saved
      ),
      Spend: formatNumber(entry.spend, { currency }),
    }));
  }, [data]);

  const CustomYAxisTick = ({ x, y, payload }: AxisTickProps) => (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="end" dy={5} fill="#66768D">
        {formatNumber(payload.value as number, { currency })}
      </text>
    </g>
  );

  const CustomXAxisTick = ({ x, y, payload }: AxisTickProps) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dx={-30}
          dy={4}
          transform="rotate(270)"
          textAnchor="middle"
          fill="#66768D"
        >
          {payload.value as string}
        </text>
      </g>
    );
  };

  const handleLegendClick = (legendData: { value?: string }) => {
    const datakey = legendData?.value;

    switch (datakey) {
      case "Spend":
        if (autoTuneFilter === "spend") {
          setAutoTuneFilter("all");
        } else {
          setAutoTuneFilter("spend");
        }
        break;
      case "Possible Savings":
        if (autoTuneFilter === "potential") {
          setAutoTuneFilter("all");
        } else {
          setAutoTuneFilter("potential");
        }
        break;
      case "Auto Tune Savings":
        if (autoTuneFilter === "actual") {
          setAutoTuneFilter("all");
        } else {
          setAutoTuneFilter("actual");
        }
        break;
      default:
        setAutoTuneFilter("all");
    }
  };

  if (data.length === 0) {
    return <RelativeComponentLoader componentHeight={40} label="Fetching warehouse cost data..." />;
  }

  const makeBarShape = (fn: (props: BarShapeProps) => JSX.Element) => (rawProps: unknown) =>
    fn(rawProps as BarShapeProps);

  return (
    <div className={styles.graph}>
      <div className={styles.info}>
        {Object.keys(topInfo).map((key, i) => {
          const info = topInfo[key];
          if (info.display === "True")
            return (
              <div
                key={i}
                className={classNames(styles.topInfo, {
                  [styles.green]: info.color === "green",
                  [styles.yellow]: info.color === "yellow",
                  [styles.light_green]: info.color === "light_green",
                })}
              >
                <span className={styles.text}>{key}:</span>
                <span className={styles.text}>{formatNumber(info.value, { currency })}</span>
              </div>
            );
        })}
        {showPeriodDropdown && (
          <div className={styles.timeframe_inside_graph}>
            <TimeFrameSelectorDropdown
              options={timeframes}
              period={timeframe}
              setPeriod={dropDownOnChange}
            />
          </div>
        )}
      </div>

      <ResponsiveContainer minHeight={400} className="mb-4">
        <BarChart data={stackedData} margin={{ top: 40, right: 50, bottom: 50 }}>
          <CartesianGrid />
          <XAxis
            dataKey="time"
            interval={0}
            label={{ value: "Time", position: "right", fill: "black" }}
            tick={<CustomXAxisTick x={0} y={0} payload={{ value: "" }} />}
          />
          <YAxis
            tick={<CustomYAxisTick x={0} y={0} payload={{ value: 0 }} />}
            label={{
              value: "Cost",
              position: "top",
              fill: "black",
              offset: 20,
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {autoTuneFilter === "all" || autoTuneFilter === "spend" ? (
            <Bar
              barSize={20}
              dataKey="spend"
              name="Spend"
              fill="#FFCE73"
              stackId="cost"
              shape={makeBarShape(props => {
                if (
                  (props.payload?.["Auto Tune Savings"] ?? 0) <= 0 &&
                  (props.payload?.["Possible Savings"] ?? 0) <= 0
                ) {
                  props.radius = [5, 5, 0, 0];
                }

                return <Rectangle {...props} />;
              })}
            />
          ) : (
            <Bar
              barSize={20}
              dataKey="s"
              name="Spend"
              fill="#FFCE73"
              stackId="cost"
              shape={makeBarShape(props => {
                if (
                  (props.payload?.["Auto Tune Savings"] ?? 0) <= 0 &&
                  (props.payload?.["Possible Savings"] ?? 0) <= 0
                ) {
                  props.radius = [5, 5, 0, 0];
                }

                return <Rectangle {...props} />;
              })}
            />
          )}
          {autoTuneFilter === "all" || autoTuneFilter === "potential" ? (
            <Bar
              barSize={20}
              dataKey="Possible Savings"
              fill="#DB9717"
              stackId="cost"
              shape={makeBarShape(props => {
                if ((props.payload?.["Auto Tune Savings"] ?? 0) <= 0) {
                  props.radius = [5, 5, 0, 0];
                }

                return <Rectangle {...props} />;
              })}
            />
          ) : (
            <Bar
              barSize={20}
              dataKey="p"
              name="Possible Savings"
              fill="#DB9717"
              stackId="cost"
              shape={makeBarShape(props => {
                if ((props.payload?.["Auto Tune Savings"] ?? 0) <= 0) {
                  props.radius = [5, 5, 0, 0];
                }

                return <Rectangle {...props} />;
              })}
            />
          )}
          {autoTuneFilter === "all" || autoTuneFilter === "actual" ? (
            <Bar
              barSize={20}
              dataKey="Auto Tune Savings"
              fill="#01CD8C"
              stackId="cost"
              radius={[5, 5, 0, 0]}
            />
          ) : (
            <Bar
              barSize={20}
              dataKey="a"
              name="Auto Tune Savings"
              fill="#01CD8C"
              stackId="cost"
              radius={[5, 5, 0, 0]}
            />
          )}
          <Legend
            onClick={handleLegendClick}
            iconType="circle"
            wrapperStyle={{ bottom: -10 }}
            formatter={value => <span className={styles.legend}>{value}</span>}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export { AutoManageGraph };
